import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const inventoryClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
});

inventoryClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

inventoryClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

// --- Shared ---

interface PaginatedResponse<T> {
  data: T[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

// --- Categories ---

export interface Category {
  id: string;
  name: string;
  description: string;
  category_type: string;
  parent_id: string | null;
  display_order: number;
  image_url: string;
  icon: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const categoryService = {
  list: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Category>> => {
    const res = await inventoryClient.get<{
      categories: Category[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>('/api/v1/categories', { params });
    return {
      data: res.data.categories,
      page: res.data.page,
      page_size: res.data.page_size,
      total_items: res.data.total,
      total_pages: res.data.total_pages,
    };
  },
  getActive: async (): Promise<Category[]> => {
    const res = await inventoryClient.get<{ categories: Category[] }>('/api/v1/categories/active');
    return res.data.categories;
  },
  create: (data: Partial<Category>) => inventoryClient.post<Category>('/api/v1/categories', data),
  update: (id: string, data: Partial<Category>) =>
    inventoryClient.put<Category>(`/api/v1/categories/${id}`, data),
  delete: (id: string) => inventoryClient.delete(`/api/v1/categories/${id}`),
};

// --- Products ---

export type ProductStatus = 'available' | 'unavailable' | 'seasonal' | 'discontinued';

export type UnitOfMeasure = 'piece' | 'kg' | 'g' | 'liter' | 'ml' | 'box' | 'pack' | 'dozen';

export interface ModifierOption {
  id?: string;
  name: string;
  price_modifier: number;
}

export interface ModifierGroup {
  name: string;
  required: boolean;
  multi_select: boolean;
  options: ModifierOption[];
}

export interface ProductRecipeItem {
  id: string;
  product_id: string;
  ingredient_stock_id: string;
  ingredient_stock?: InventoryItem;
  quantity_required: number;
  unit: UnitOfMeasure;
}

export interface Product {
  id: string;
  sku: string;
  barcode: string;
  name: string;
  description: string;
  category_id: string;
  category?: Category;
  price: number;
  cost_price: number;
  tax_rate: number;
  status: ProductStatus;
  image_url: string;
  unit_of_measure: UnitOfMeasure;
  modifier_groups: ModifierGroup[];
  allergens: string[];
  prep_time_minutes: number;
  preparation_notes: string;
  is_featured: boolean;
  is_popular: boolean;
  tracks_inventory: boolean;
  has_recipe: boolean;
  display_order: number;
  stock_item_id: string | null;
  stock_item?: InventoryItem;
  recipes?: ProductRecipeItem[];
  location: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProductInput {
  sku: string;
  name: string;
  category_id: string;
  price: number;
  cost_price?: number;
  tax_rate?: number;
  unit_of_measure?: UnitOfMeasure;
  barcode?: string;
  description?: string;
  image_url?: string;
  allergens?: string[];
  prep_time_minutes?: number;
  is_featured?: boolean;
  is_popular?: boolean;
  status?: string;
  tracks_inventory?: boolean;
  has_recipe?: boolean;
  stock_item_id?: string;
  recipe_items?: Array<{
    ingredient_stock_id: string;
    quantity_required: number;
    unit?: UnitOfMeasure;
  }>;
  modifier_groups?: Array<{
    name: string;
    required?: boolean;
    multi_select?: boolean;
    options: Array<{ name: string; price_modifier?: number }>;
  }>;
}

export interface UpdateProductInput {
  sku?: string;
  name?: string;
  category_id?: string;
  price?: number;
  cost_price?: number;
  tax_rate?: number;
  unit_of_measure?: UnitOfMeasure;
  barcode?: string;
  description?: string;
  image_url?: string;
  allergens?: string[];
  prep_time_minutes?: number;
  is_featured?: boolean;
  is_popular?: boolean;
  status?: ProductStatus;
  tracks_inventory?: boolean;
  has_recipe?: boolean;
  stock_item_id?: string;
  recipe_items?: Array<{
    ingredient_stock_id: string;
    quantity_required: number;
    unit?: UnitOfMeasure;
  }>;
}

export const productService = {
  list: async (params?: {
    category_id?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Product>> => {
    const res = await inventoryClient.get<{
      products: Product[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>('/api/v1/products', { params });
    return {
      data: res.data.products,
      page: res.data.page,
      page_size: res.data.page_size,
      total_items: res.data.total,
      total_pages: res.data.total_pages,
    };
  },
  get: (id: string) => inventoryClient.get<Product>(`/api/v1/products/${id}`),
  create: (data: CreateProductInput) => inventoryClient.post<Product>('/api/v1/products', data),
  update: (id: string, data: UpdateProductInput) =>
    inventoryClient.put<Product>(`/api/v1/products/${id}`, data),
  delete: (id: string) => inventoryClient.delete(`/api/v1/products/${id}`),
  toggleAvailability: (id: string) =>
    inventoryClient.patch<{ status: string; message: string }>(
      `/api/v1/products/${id}/toggle-availability`,
    ),
  getByBarcode: (barcode: string) =>
    inventoryClient.get<Product>(`/api/v1/products/barcode/${encodeURIComponent(barcode)}`),
};

// --- Inventory Items ---

export type InventoryLocation = 'store' | 'bar' | 'kitchen' | 'restaurant' | 'room';

export interface InventoryItem {
  id: string;
  product_id: string | null;
  product?: Product;
  sku: string;
  name: string;
  current_quantity: number;
  minimum_quantity: number;
  maximum_quantity: number;
  reorder_point: number;
  unit_of_measure: UnitOfMeasure;
  supplier_id: string | null;
  supplier?: Supplier;
  location: InventoryLocation;
  storage_location: string;
  unit_cost: number;
  is_active: boolean;
  requires_tracking: boolean;
  last_counted_at: string | null;
  last_received_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateInventoryItemInput {
  sku: string;
  name: string;
  location: InventoryLocation;
  product_id?: string;
  current_quantity?: number;
  minimum_quantity?: number;
  reorder_point?: number;
  unit_of_measure?: UnitOfMeasure;
  supplier_id?: string;
  unit_cost?: number;
}

export interface AdjustStockInput {
  quantity: number;
  reason: string;
}

export interface TransferStockInput {
  to_location: InventoryLocation;
  quantity: number;
  reason?: string;
}

export const inventoryService = {
  list: async (params?: {
    location?: string;
    low_stock?: boolean;
    products_only?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<InventoryItem>> => {
    const res = await inventoryClient.get<{
      items: InventoryItem[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>('/api/v1/inventory', { params });
    return {
      data: res.data.items,
      page: res.data.page,
      page_size: res.data.page_size,
      total_items: res.data.total,
      total_pages: res.data.total_pages,
    };
  },
  get: (id: string) => inventoryClient.get<InventoryItem>(`/api/v1/inventory/${id}`),
  create: (data: Partial<CreateInventoryItemInput>) =>
    inventoryClient.post<InventoryItem>('/api/v1/inventory', data),
  update: (id: string, data: Partial<CreateInventoryItemInput>) =>
    inventoryClient.put<InventoryItem>(`/api/v1/inventory/${id}`, data),
  delete: (id: string) => inventoryClient.delete(`/api/v1/inventory/${id}`),
  getLowStock: async (): Promise<InventoryItem[]> => {
    const res = await inventoryClient.get<{ items: InventoryItem[] }>(
      '/api/v1/inventory/low-stock',
    );
    return res.data.items;
  },
  adjust: (id: string, data: AdjustStockInput) =>
    inventoryClient.post<InventoryItem>(`/api/v1/inventory/${id}/adjust`, data),
  transfer: (id: string, data: TransferStockInput) =>
    inventoryClient.post<InventoryItem>(`/api/v1/inventory/${id}/transfer`, data),
  getTransactions: (id: string) =>
    inventoryClient
      .get<{ transactions: any[] }>(`/api/v1/inventory/${id}/transactions`)
      .then(res => res.data),
};

// --- Suppliers ---

export type SupplierStatus = 'active' | 'inactive' | 'pending';

export interface Supplier {
  id: string;
  name: string;
  code: string;
  contact_person: string;
  phone: string;
  email: string;
  website: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  tax_pin: string;
  registration_number: string;
  payment_terms: string;
  credit_limit: number;
  status: SupplierStatus;
  rating: number;
  lead_time_days: number;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface CreateSupplierInput {
  name: string;
  code: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address_line1?: string;
  city?: string;
  country?: string;
  payment_terms?: string;
  credit_limit?: number;
  lead_time_days?: number;
}

export interface UpdateSupplierInput {
  name?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address_line1?: string;
  city?: string;
  country?: string;
  payment_terms?: string;
  credit_limit?: number;
  lead_time_days?: number;
  status?: SupplierStatus;
}

export const supplierService = {
  list: async (params?: {
    status?: SupplierStatus;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Supplier>> => {
    const res = await inventoryClient.get<{
      suppliers: Supplier[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>('/api/v1/suppliers', { params });
    return {
      data: res.data.suppliers,
      page: res.data.page,
      page_size: res.data.page_size,
      total_items: res.data.total,
      total_pages: res.data.total_pages,
    };
  },
  get: (id: string) => inventoryClient.get<Supplier>(`/api/v1/suppliers/${id}`),
  create: (data: CreateSupplierInput) => inventoryClient.post<Supplier>('/api/v1/suppliers', data),
  update: (id: string, data: UpdateSupplierInput) =>
    inventoryClient.put<Supplier>(`/api/v1/suppliers/${id}`, data),
  delete: (id: string) => inventoryClient.delete(`/api/v1/suppliers/${id}`),
};

// --- Purchase Orders ---

export type PurchaseOrderStatus = 'draft' | 'sent' | 'partial' | 'received' | 'cancelled';

export interface PurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  inventory_item_id: string | null;
  product_id: string | null;
  item_name: string;
  sku: string;
  quantity_ordered: number;
  quantity_received: number;
  quantity_invoiced: number;
  unit_cost: number;
  total_cost: number;
  is_fully_received: boolean;
  notes: string;
}

export interface PurchaseOrder {
  id: string;
  po_number: string;
  supplier_id: string;
  supplier?: Supplier;
  order_date: string;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  status: PurchaseOrderStatus;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  delivery_address: string;
  delivery_notes: string;
  payment_status: string;
  payment_reference: string;
  notes: string;
  internal_notes: string;
  accountant_approved_by: string | null;
  accountant_approved_at: string | null;
  accountant_rejection_reason: string | null;
  cancellation_notes: string | null;
  payment_history: string | null;
  items: PurchaseOrderItem[];
  created_at: string;
  updated_at: string;
}

export interface CreatePOItemInput {
  item_name: string;
  quantity_ordered: number;
  unit_cost: number;
  sku?: string;
  product_id?: string;
  inventory_item_id?: string;
}

export interface CreatePOInput {
  supplier_id: string;
  expected_delivery_date?: string;
  delivery_address?: string;
  delivery_notes?: string;
  items: CreatePOItemInput[];
  notes?: string;
}

export interface ReceivePOItemInput {
  item_id: string;
  quantity: number;

  batch_number?: string;
  lot_number?: string;
}

export interface ReceivePOInput {
  items: ReceivePOItemInput[];
}

export const purchaseOrderService = {
  list: async (params?: {
    supplier_id?: string;
    status?: PurchaseOrderStatus;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<PurchaseOrder>> => {
    const res = await inventoryClient.get<{
      purchase_orders: PurchaseOrder[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>('/api/v1/purchase-orders', { params });
    return {
      data: res.data.purchase_orders,
      page: res.data.page,
      page_size: res.data.page_size,
      total_items: res.data.total,
      total_pages: res.data.total_pages,
    };
  },
  get: (id: string) => inventoryClient.get<PurchaseOrder>(`/api/v1/purchase-orders/${id}`),
  create: (data: CreatePOInput) =>
    inventoryClient.post<PurchaseOrder>('/api/v1/purchase-orders', data),
  update: (id: string, data: Partial<CreatePOInput>) =>
    inventoryClient.put<PurchaseOrder>(`/api/v1/purchase-orders/${id}`, data),
  delete: (id: string) => inventoryClient.delete(`/api/v1/purchase-orders/${id}`),
  receive: (id: string, data: ReceivePOInput) =>
    inventoryClient.post(`/api/v1/purchase-orders/${id}/receive`, data),
  cancel: (id: string, reason?: string) =>
    inventoryClient.post(`/api/v1/purchase-orders/${id}/cancel`, { reason }),
  updateStatus: (id: string, status: PurchaseOrderStatus) =>
    inventoryClient.patch<PurchaseOrder>(`/api/v1/purchase-orders/${id}/status`, { status }),
  approve: (id: string) => inventoryClient.post(`/api/v1/purchase-orders/${id}/approve`),
  reject: (id: string, reason: string) =>
    inventoryClient.post(`/api/v1/purchase-orders/${id}/reject`, { reason }),
  updatePayment: (id: string, paymentStatus: string, paymentReference: string) =>
    inventoryClient.patch(`/api/v1/purchase-orders/${id}/payment`, {
      payment_status: paymentStatus,
      payment_reference: paymentReference,
    }),
};

export interface InventoryValuation {
  total_value: number;
  total_items: number;
  by_location: { location: string; value: number; count: number }[];
  by_category: { category: string; value: number; count: number }[];
  items: {
    id: string;
    sku: string;
    name: string;
    quantity: number;
    unit_cost: number;
    total_value: number;
    location: string;
    minimum_quantity: number;
    reorder_point: number;
  }[];
}

export const inventoryValuationService = {
  get: () => inventoryClient.get<InventoryValuation>('/api/v1/finance/inventory-valuation'),
};

// --- Asset Tracking ---

export interface Asset {
  id: string;
  name: string;
  category: string;
  quantity: number;
  location: string;
  condition: 'new' | 'good' | 'fair' | 'poor' | 'damaged';
  is_movable: boolean;
  is_unique: boolean;
  serial_number?: string;
  notes?: string;
  linked_to_type?: string;
  linked_to_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AssetAssignment {
  id: string;
  asset_id: string;
  asset?: Asset;
  assigned_to: string;
  assigned_at: string;
  returned_at?: string;
  quantity: number;
  purpose?: string;
  created_at: string;
}

export interface AssetMovement {
  id: string;
  asset_id: string;
  asset?: Asset;
  from_location: string;
  to_location: string;
  moved_by: string;
  notes?: string;
  created_at: string;
}

export interface AssetIncident {
  id: string;
  asset_id: string;
  asset?: Asset;
  type: string;
  description: string;
  reported_by: string;
  resolved: boolean;
  resolved_at?: string;
  created_at: string;
}

export const assetService = {
  list: (params?: Record<string, string | number>) =>
    inventoryClient.get('/api/v1/assets', { params }),
  get: (id: string) => inventoryClient.get<Asset>(`/api/v1/assets/${id}`),
  create: (data: Partial<Asset>) => inventoryClient.post<Asset>('/api/v1/assets', data),
  update: (id: string, data: Partial<Asset>) =>
    inventoryClient.put<Asset>(`/api/v1/assets/${id}`, data),
  delete: (id: string) => inventoryClient.delete(`/api/v1/assets/${id}`),

  assign: (assetId: string, data: Partial<AssetAssignment>) =>
    inventoryClient.post<AssetAssignment>(`/api/v1/assets/${assetId}/assign`, data),
  listAssignments: (assetId: string) =>
    inventoryClient.get(`/api/v1/assets/${assetId}/assignments`),
  returnAssignment: (assignmentId: string) =>
    inventoryClient.post(`/api/v1/assets/assignments/${assignmentId}/return`),

  move: (assetId: string, data: Partial<AssetMovement>) =>
    inventoryClient.post<AssetMovement>(`/api/v1/assets/${assetId}/move`, data),
  listMovements: (assetId: string) => inventoryClient.get(`/api/v1/assets/${assetId}/movements`),

  reportIncident: (assetId: string, data: Partial<AssetIncident>) =>
    inventoryClient.post<AssetIncident>(`/api/v1/assets/${assetId}/incidents`, data),
  listIncidents: (assetId: string) => inventoryClient.get(`/api/v1/assets/${assetId}/incidents`),
  resolveIncident: (incidentId: string) =>
    inventoryClient.post(`/api/v1/assets/incidents/${incidentId}/resolve`),
};

// --- Stock Counts ---

export interface StockCountItem {
  id: string;
  count_id: string;
  inventory_item_id: string;
  system_quantity: number;
  counted_quantity: number;
  variance: number;
  notes?: string;
  is_recounted: boolean;
  created_at: string;
}

export interface StockCount {
  id: string;
  location: string;
  status: 'draft' | 'in_progress' | 'reviewed' | 'approved';
  started_by: string;
  started_at: string;
  completed_at?: string;
  approved_by?: string;
  approved_at?: string;
  notes?: string;
  created_at: string;
  items?: StockCountItem[];
}

export const stockCountService = {
  list: (params?: Record<string, string | number>) =>
    inventoryClient.get<StockCount[]>('/api/v1/stock-counts', { params }),
  get: (id: string) => inventoryClient.get<StockCount>(`/api/v1/stock-counts/${id}`),
  create: (data: { location: string; notes?: string }) =>
    inventoryClient.post<StockCount>('/api/v1/stock-counts', data),
  addItem: (
    countId: string,
    data: {
      inventory_item_id: string;
      system_quantity: number;
      counted_quantity: number;
      notes?: string;
    },
  ) => inventoryClient.post<StockCountItem>(`/api/v1/stock-counts/${countId}/items`, data),
  complete: (countId: string) =>
    inventoryClient.post<StockCount>(`/api/v1/stock-counts/${countId}/complete`),
  approve: (countId: string) =>
    inventoryClient.post<StockCount>(`/api/v1/stock-counts/${countId}/approve`),
};

export interface InventoryBatch {
  id: string;
  inventory_item_id: string;
  batch_number: string;
  lot_number?: string;
  quantity: number;
  unit_cost: number;
  received_date: string;
  is_active: boolean;
  created_at: string;
}

export const batchService = {
  create: (data: Partial<InventoryBatch>) =>
    inventoryClient.post<InventoryBatch>('/api/v1/batches', data),
  listByItem: (itemId: string) =>
    inventoryClient.get<InventoryBatch[]>(`/api/v1/batches/item/${itemId}`),
  getQR: (id: string) => inventoryClient.get(`/api/v1/batches/${id}/qr`),
  getItemQR: (itemId: string) => inventoryClient.get(`/api/v1/batches/item/${itemId}/qr`),
  waste: (id: string) => inventoryClient.post(`/api/v1/batches/${id}/waste`),
};

export interface MealPackage {
  id: string;
  name: string;
  description: string;
  product_ids: string[];
  created_at: string;
  updated_at: string;
}

export const mealPackageService = {
  list: () =>
    inventoryClient
      .get<{ meal_packages: MealPackage[] }>('/api/v1/meal-packages')
      .then(r => r.data),

  get: (id: string) =>
    inventoryClient.get<MealPackage>(`/api/v1/meal-packages/${id}`).then(r => r.data),

  create: (data: { name: string; description?: string; product_ids: string[] }) =>
    inventoryClient.post<MealPackage>('/api/v1/meal-packages', data).then(r => r.data),

  update: (id: string, data: { name?: string; description?: string; product_ids?: string[] }) =>
    inventoryClient.put<MealPackage>(`/api/v1/meal-packages/${id}`, data).then(r => r.data),

  delete: (id: string) => inventoryClient.delete(`/api/v1/meal-packages/${id}`).then(r => r.data),
};
